import { JetView } from 'webix-jet';
import { devConfig, profile } from 'config/config';

/**
 * FilterIncomesView class extends JetView to create a filter form for income records.
 */
export default class FilterIncomesView extends JetView {
    /**
     * Configures the layout of the filter form.
     * @returns {Object} The configuration object for the form.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const screen = this.app.config.size; // Screen size configuration

        // Left section of the form with input fields
        const left = {
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    cols: [
                        {
                            view: 'text', // Input for consecutive number
                            name: 'Consecutive',
                            type: 'text',
                            label: "<span class='icon-drivers-license-o'></span>" + _('Consecutivo'),
                            labelPosition: 'top',
                            placeholder: _('Consecutivo'),
                        },
                        {
                            view: 'text', // Readonly datepicker input
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange',
                            readonly: true,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de Creación'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                        },
                        {
                            view: 'datepicker', // Date selection input
                            name: 'Date',
                            id: 'Date',
                            stringResult: true,
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            value: moment().format('YYYY-MM-DD'), // Default date value
                            format: '%d %M %Y',
                            suggest: {
                                type: 'calendar',
                                body: {
                                    maxDate: new Date(), // Maximum selectable date
                                },
                            },
                        },
                        {
                            view: 'radio', // Radio buttons for credit card option
                            name: 'CreditCards',
                            label: _('Tarjeta de crédito'),
                            labelPosition: 'top',
                            value: 0,
                            options: [
                                { id: 0, value: _('Sí') },
                                { id: 1, value: _('No'), default: true },
                            ],
                        },
                    ],
                },
            ],
        };

        // Right section of the form (currently empty)
        const right = {
            margin: 5,
            minWidth: 200,
            rows: [],
        };

        // Additional left section with income type and document ID inputs
        const left2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'combo', // Dropdown for income type
                    name: 'TypeIncome',
                    id: 'TypeIncomeFilter',
                    label: _('Tipo de Ingreso'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                },
                {
                    view: 'text', // Input for document ID
                    name: 'DocumentID',
                    type: 'number',
                    label: _('Documento ID'),
                    labelPosition: 'top',
                    placeholder: _('Documento ID'),
                },
            ],
        };

        // Center left section with bet shop and activation status inputs
        const center_left2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'combo', // Dropdown for bet shop selection
                    name: 'BetShop',
                    id: 'BetShopFilter',
                    label: "<span class='icon-shop'></span>" + _('Punto de Venta'),
                    hidden: !devConfig.getPermAction('filterByBetShop', 'Add'), // Conditional visibility
                    css: 'prm-filterByBetShop',
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                },
                {
                    view: 'richselect', // Dropdown for activation status
                    name: 'IsActivate',
                    label: "<span class='icon-state'></span>" + _('Estado'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                    options: [
                        { id: 'null', value: _('Todos') },
                        { id: 'A', value: _('Activado') },
                        { id: 'I', value: _('Inactivado') },
                    ],
                },
            ],
        };

        // Center right section with cost center input
        const center_right2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'combo', // Dropdown for cost center selection
                    name: 'CostCenter',
                    id: 'CostCenterFilter',
                    label: _('Centro de Costos'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                },
            ],
        };

        // Right section with concepts input
        const right2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'combo', // Dropdown for concepts selection
                    name: 'Concepts',
                    id: 'ConceptsFilter',
                    label: _('Concepto'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                },
            ],
        };

        // Upper section of the form
        const upper_section = {
            css: 'father-container',
            height: document.body.offsetWidth < 500 ? 180 : 0,
            rows: [
                {
                    margin: 10,
                    cols: [left], // Contains the left section
                },
            ],
        };

        // Second upper section with additional filters
        const upper_section2 = {
            css: 'father-container',
            height: document.body.offsetWidth < 500 ? 360 : 0,
            id: 'a1',
            rows: [
                {
                    responsive: 'a1',
                    margin: 10,
                    cols: [left2, center_left2, center_right2, right2], // Contains multiple sections
                },
            ],
        };

        // Accordion section for advanced filters
        const accordion_section = {
            id: 'b5',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: true,
                    paddingY: 10,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros Avanzados'),
                            body: {
                                padding: 10,
                                rows: [upper_section2], // Contains the second upper section
                            },
                        },
                    ],
                },
            ],
        };

        // Buttons section for form actions
        const buttons = {
            id: 'b2',
            rows: [
                {
                    margin: 10,
                    responsive: 'b2',
                    cols: [
                        { id: 'grav', gravity: 4 }, // Placeholder for layout
                        {
                            view: 'button', // Clear button
                            value: _('Limpiar'),
                            width: screen == 'small' ? 0 : 100,
                            minWidth: 200,
                            click: () => {
                                this.getRoot().clear(); // Clears the form inputs
                            },
                        },
                        {
                            view: 'button', // Consult button
                            value: _('Consultar'),
                            hotkey: 'enter',
                            type: 'form',
                            width: screen == 'small' ? 0 : 100,
                            minWidth: 200,
                            click: () => {
                                const filterdata = this.getRoot().getValues(); // Get form values
                                if (profile.typeDate !== 2) {
                                    delete filterdata['Date']; // Remove date if not applicable
                                } else {
                                    if (true) {
                                        delete filterdata['Datepicker']; // Remove datepicker if applicable
                                    }
                                }
                                this.app.callEvent('incomes:filter', [filterdata]); // Trigger filter event
                            },
                        },
                    ],
                },
            ],
        };

        // Returns the complete form configuration
        return {
            view: 'form',
            rows: [upper_section, accordion_section, buttons],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     */
    init() {
        webix.ready(function () {
            if (screen.width < 500) {
                $$('grav').hide(); // Hide element on small screens
            }
            if (profile.typeDate !== 2) {
                $$('Date').hide(); // Hide date input if not applicable
                $$('Datepicker').show(); // Show datepicker
            } else {
                if (true) {
                    $$('Date').show(); // Show date input if applicable
                    $$('Datepicker').hide(); // Hide datepicker
                }
            }
        });

        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            devConfig.setDateModal(lang); // Set date modal based on language
        });

        // AJAX request to fetch types of center position
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'Accounting/GetTypesCenterPositionSelect', '{"Type":3}', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message('Error! '); // Error handling
                },
                success: function (text, data, XmlHttpRequest) {
                    var resp = JSON.parse(text); // Parse response
                    $$('TypeIncomeFilter').define('options', resp.Data); // Set options for income type filter
                    $$('TypeIncomeFilter').refresh(); // Refresh dropdown
                },
            });

        // AJAX request to fetch bet shop options
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'Accounting/GetBetShopSelect', '', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message('Error! '); // Error handling
                },
                success: function (text, data, XmlHttpRequest) {
                    var resp = JSON.parse(text); // Parse response
                    $$('BetShopFilter').define('options', resp.Data); // Set options for bet shop filter
                    $$('BetShopFilter').refresh(); // Refresh dropdown
                },
            });

        // AJAX request to fetch cost center options
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'Accounting/GetCostCenterSelect', '', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message('Error! '); // Error handling
                },
                success: function (text, data, XmlHttpRequest) {
                    var resp = JSON.parse(text); // Parse response
                    $$('CostCenterFilter').define('options', resp.Data); // Set options for cost center filter
                    $$('CostCenterFilter').refresh(); // Refresh dropdown
                },
            });

        // AJAX request to fetch concepts options
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'Accounting/GetConceptsSelect', '', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message('Error! '); // Error handling
                },
                success: function (text, data, XmlHttpRequest) {
                    var resp = JSON.parse(text); // Parse response
                    $$('ConceptsFilter').define('options', resp.Data); // Set options for concepts filter
                    $$('ConceptsFilter').refresh(); // Refresh dropdown
                },
            });
    }
}

// FILE DOCUMENTED
